/*
  ecw-gift.js: free items added to the cart by themselves (offers step 3; simplified 20 Oct 2026, Alex's review H4).
  Loaded deferred by snippets/ecw-gift-script.liquid only while a free-item promotion record exists or the cart holds
  a free line. After every cart change, whatever made it, it reads one snapshot of the cart and the live records from
  sections/ecw-gift-data.liquid (Section Rendering API) and holds each record's free line at exactly the earned units:
  floor(qualifying units / buy_quantity), capped at max_uses, times get_quantity.

  Rules it keeps:
  - A free line is the line whose `_promotion` property is the record's handle. Lines are found by that property,
    never by position; any other line is never touched, so a paid line of the same variant stays paid.
  - It only adds, resizes and removes that line. The free item is the record's first available `get_variants` entry.
  - One line per record, at the earned quantity. Shopify can show that line as two pieces of the same variant and
    property when its discount covers only some of the units (a paid line of the same variant can take a free unit's
    discount, desk's preview #1682); the pieces count as the one line, so a split that adds up writes nothing.
  - One cart request at a time (one queue); a pass that finds the cart right writes nothing; a pass never starts
    another one, since its own requests go through the browser's fetch, not the watched one.
  - One pass at a time across tabs too (a Web Lock where the browser has them, given up after LOCK_WAIT), and a pass
    that wrote reads the cart again and corrects it until nothing is left to write (at most ROUNDS writes, and no
    further once a round changes nothing). A lock ends with its page, so a write still in flight from a page just
    left (a change then a reload, desk's preview #1682) can land after the new page's pass. So a pass that writes
    leaves a short-lived mark in localStorage, and a pass that finds another page's fresh mark waits for it to clear
    (at most QUIET) before it reads; and the checkout button always runs one more pass before it goes to checkout.
  - A refused add (sold out, quantity rules) leaves the cart as it is and shows a short notice in the cart
    (snippet ecw-gift-notice, aria-live).
  It never changes a price: Shopify's discount prices the free line at checkout.
*/
(function () {
  if (window.ecwGift || !window.fetch) return;

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var DATA_URL = ROOT + 'cart?section_id=ecw-gift-data';
  var MUTATION = /\/cart\/(add|change|update|clear)(\.js|\.json)?\/?(\?|#|$)/;
  var WAIT = 300; /* ms after the last cart change before a pass */
  var ROUNDS = 3; /* most write rounds in one pass */
  var LOCK = 'ecw-gift-pass';
  var LOCK_WAIT = 10000; /* ms to wait for another tab's pass before running without the lock */
  var MARK = 'ecw-gift-writing';
  var QUIET = 3000; /* ms a write mark from another page holds back a pass */
  var nativeFetch = window.fetch.bind(window);

  /* Copy from locales/en.default.json (ecw_offers), printed by snippet ecw-gift-script. */
  var text = {};
  try {
    var textNode = document.getElementById('ecw-gift-text');
    text = textNode ? JSON.parse(textNode.textContent) : {};
  } catch (error) {
    text = {};
  }
  function t(key, name) {
    var value = name || text.item || '';
    return String(text[key] || '').replace('[name]', function () { return value; });
  }

  /* ---------- times: the browser half of "live", shared with assets/ecw-offers.js ---------- */

  function toTime(value) {
    if (!value) return NaN;
    return Date.parse(String(value).replace(/([+-]\d\d)(\d\d)$/, '$1:$2'));
  }

  function liveAt(starts, ends, now) {
    if (window.ecwOffers && window.ecwOffers.liveAt) return window.ecwOffers.liveAt(starts, ends, now);
    var start = toTime(starts);
    if (!isNaN(start) && start > now) return false;
    if (!ends) return true;
    var end = toTime(ends);
    return isNaN(end) || end > now;
  }

  /* ---------- reading ---------- */

  function log() {
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[ecw-gift]');
    console.warn.apply(console, args);
  }

  function readState() {
    return nativeFetch(DATA_URL + '&ecw=' + Date.now(), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'text/html' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('gift data ' + response.status);
        return response.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var node = doc.getElementById('ecw-gift-json');
        if (!node) throw new Error('gift data missing');
        var state = JSON.parse(node.textContent);
        state.items = state.items || [];
        state.records = state.records || [];
        return state;
      });
  }

  function positive(value) {
    var n = Number(value);
    return isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }

  /* Free units a record earns in this cart. */
  function earnedUnits(record) {
    var buy = positive(record.buy_quantity);
    var get = positive(record.get_quantity);
    if (!buy || !get) return 0;
    var rewards = Math.floor(positive(record.qualifying) / buy);
    var cap = positive(record.max_uses);
    if (cap && rewards > cap) rewards = cap;
    return rewards * get;
  }

  function liveRecords(state) {
    var now = Date.now();
    var live = {};
    state.records.forEach(function (record) {
      if (record && record.handle && liveAt(record.starts, record.ends, now)) live[record.handle] = record;
    });
    return live;
  }

  function giftLines(state) {
    var lines = {};
    state.items.forEach(function (item) {
      if (!item.promotion) return;
      (lines[item.promotion] = lines[item.promotion] || []).push(item);
    });
    return lines;
  }

  function giftIds(record) {
    return (record.gifts || []).map(function (gift) { return Number(gift.id); });
  }

  /* The free item to add: the record's first available variant, as Customer POs picks it. */
  function firstAvailable(record) {
    return (record.gifts || []).filter(function (gift) { return gift.available; })[0] || null;
  }

  function isAvailable(record, variantId) {
    return (record.gifts || []).some(function (gift) { return Number(gift.id) === Number(variantId) && gift.available; });
  }

  function giftName(gift) {
    if (!gift) return '';
    var name = gift.product || gift.title || '';
    if (gift.product && gift.title && gift.title !== gift.product) name += ', ' + gift.title;
    return name;
  }

  /* The one line kept for a record: the first whose variant is still one of its gifts. */
  function keptLine(record, lines) {
    var ids = giftIds(record);
    for (var i = 0; i < lines.length; i++) {
      if (ids.indexOf(Number(lines[i].variant_id)) > -1) return lines[i];
    }
    return null;
  }

  /* The kept line as Shopify lists it: every piece of the same variant for the record (one, unless split). */
  function pieces(kept, lines) {
    return lines.filter(function (line) { return Number(line.variant_id) === Number(kept.variant_id); });
  }

  function units(lines) {
    return lines.reduce(function (sum, line) { return sum + positive(line.quantity); }, 0);
  }

  /* What one pass writes: quantities by line key (0 removes), lines to add, free items that cannot be added, and
     the free line totals to read back after the write. */
  function plan(state) {
    var live = liveRecords(state);
    var lines = giftLines(state);
    var updates = {};
    var adds = [];
    var missing = [];
    var checks = [];

    Object.keys(live).forEach(function (handle) {
      var record = live[handle];
      var own = lines[handle] || [];
      var target = earnedUnits(record);
      var kept = keptLine(record, own);
      var gift = firstAvailable(record);

      /* A free line on a variant that has sold out moves to the first available one. */
      if (kept && target > 0 && gift && Number(gift.id) !== Number(kept.variant_id) && !isAvailable(record, kept.variant_id)) {
        kept = null;
      }

      var keep = kept ? pieces(kept, own) : [];
      var settled = kept && units(keep) === target;
      own.forEach(function (line) {
        if (settled ? keep.indexOf(line) < 0 : line !== kept) updates[line.key] = 0;
      });
      if (kept) {
        if (!settled) {
          updates[kept.key] = target;
          if (target > 0) checks.push({ handle: handle, variant: Number(kept.variant_id), quantity: target });
        }
      } else if (target > 0) {
        if (gift) adds.push({ id: Number(gift.id), quantity: target, properties: { _promotion: handle }, name: giftName(gift) });
        else missing.push(giftName((record.gifts || [])[0]));
      }
    });

    /* A free line whose record has ended or gone would be charged in full: it goes. */
    Object.keys(lines).forEach(function (handle) {
      if (live[handle]) return;
      lines[handle].forEach(function (line) { updates[line.key] = 0; });
    });

    return { updates: updates, adds: adds, missing: missing, checks: checks };
  }

  /* ---------- writing ---------- */

  function post(path, body) {
    return nativeFetch(ROOT + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().catch(function () { return {}; }).then(function (data) {
        if (!response.ok) {
          var error = new Error(data.description || data.message || path + ' ' + response.status);
          error.data = data;
          throw error;
        }
        return data;
      });
    });
  }

  /* Writes one plan; resolves to { wrote, notices } so the cart can say what could not be added. */
  function apply(steps) {
    var wrote = false;
    var notices = steps.missing.map(function (name) { return t('sold_out', name); });
    var run = Promise.resolve();
    if (Object.keys(steps.updates).length) {
      run = run.then(function () {
        wrote = true;
        return post('cart/update.js', { updates: steps.updates }).then(function (cart) {
          var items = (cart && cart.items) || [];
          /* Read back the free line's total over its pieces: Shopify may list the line split in two. */
          (steps.checks || []).forEach(function (check) {
            var held = items.filter(function (item) {
              return item.properties && item.properties._promotion === check.handle && Number(item.variant_id) === check.variant;
            });
            if (units(held) !== check.quantity) {
              log('free line held at', units(held), 'not', check.quantity);
              notices.push(t('refused', held.length ? held[0].product_title : ''));
            }
          });
        });
      });
    }
    steps.adds.forEach(function (line) {
      run = run.then(function () {
        wrote = true;
        return post('cart/add.js', { items: [{ id: line.id, quantity: line.quantity, properties: line.properties }] })
          .catch(function (error) {
            log('could not add the free item', line.id, 'for', line.properties._promotion, error.message);
            notices.push(t('refused', line.name));
          });
      });
    });
    return run.then(function () { return { wrote: wrote, notices: notices }; });
  }

  /* Re-reads the cart into the theme's own drawer and cart page (a GET, so no new pass). */
  function refreshTheme() {
    try {
      guardVariantMap();
      var app = window.Alpine && window.Alpine.$data(document.documentElement);
      if (app && app.updateCart) app.updateCart(false, false);
    } catch (error) {
      log('could not refresh the cart', error);
    }
  }

  /* ---------- the queue ---------- */

  var chain = Promise.resolve();
  var pending = 0;
  var timer = null;

  function enqueue(task) {
    pending++;
    var run = chain.then(task).catch(function (error) {
      log(error && error.message ? error.message : error);
    }).then(function () {
      pending--;
    });
    chain = run;
    return run;
  }

  function busy() {
    return pending > 0 || timer !== null;
  }

  function nothingToWrite(steps) {
    return !Object.keys(steps.updates).length && !steps.adds.length;
  }

  /* The write mark: when this page's pass last started writing (ms), or 0. Storage may be unavailable (private
     modes): then there is no mark and no wait. */
  var ownMark = 0;
  function readMark() {
    try {
      return Number(window.localStorage.getItem(MARK)) || 0;
    } catch (error) {
      return 0;
    }
  }
  function setMark(on) {
    try {
      if (on) {
        ownMark = Date.now();
        window.localStorage.setItem(MARK, String(ownMark));
      } else {
        if (readMark() === ownMark) window.localStorage.removeItem(MARK);
        ownMark = 0;
      }
    } catch (error) {
      ownMark = 0;
    }
  }

  /* Waits while another page's write may still be landing: its mark is fresh and not ours. */
  function quiet() {
    var started = Date.now();
    return new Promise(function (resolve) {
      (function check() {
        var mark = readMark();
        var fresh = mark && mark !== ownMark && Date.now() - mark < QUIET;
        if (!fresh || Date.now() - started >= QUIET) resolve();
        else setTimeout(check, 100);
      })();
    });
  }

  /* Runs task alone: other tabs wait for it (Web Locks; Safari 15.4 and up). A lock not granted in LOCK_WAIT, or
     refused, runs the task without it, so one stuck tab never stalls the others. */
  function alone(task) {
    var started = false;
    function run() {
      started = true;
      return task();
    }
    try {
      if (navigator.locks && navigator.locks.request) {
        var options = window.AbortSignal && AbortSignal.timeout ? { signal: AbortSignal.timeout(LOCK_WAIT) } : {};
        return navigator.locks.request(LOCK, options, run).catch(function (error) {
          if (started) throw error;
          log('cart lock not granted', error && error.name);
          return run();
        });
      }
    } catch (error) {
      log('no cart lock', error);
    }
    return run();
  }

  /* One reconcile pass: alone across tabs, and only once another page's write has had time to land. */
  function pass() {
    setNotice('');
    return alone(function () {
      return quiet().then(reconcile);
    });
  }

  /* One reconcile: read, write what differs, read again until the cart is right, say what could not be added,
     refresh. */
  function reconcile() {
    var wrote = false;
    var notices = [];
    var last = '';
    function round(left) {
      return readState().then(function (state) {
        publish(state);
        var steps = plan(state);
        /* Settled: only a sold-out free item is left to say. Out of rounds, or the same write as the round
           before (a refusal that stands): the last write's notices stand. */
        if (nothingToWrite(steps)) {
          notices = steps.missing.map(function (name) { return t('sold_out', name); });
          return;
        }
        var shape = JSON.stringify([steps.updates, steps.adds]);
        if (!left || shape === last) return;
        last = shape;
        setMark(true);
        return apply(steps).then(function (result) {
          wrote = true;
          notices = result.notices;
          return round(left - 1);
        });
      });
    }
    function finish() {
      setMark(false);
      setNotice(notices.filter(Boolean).join(' '));
      if (wrote) refreshTheme();
    }
    return round(ROUNDS).then(finish, function (error) {
      finish();
      throw error;
    });
  }

  function schedule() {
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(function () {
      timer = null;
      enqueue(pass);
    }, WAIT);
  }

  /* ---------- the Alpine store the cart templates read (snippets ecw-gift-line, ecw-gift-notice) ---------- */

  var store = {
    records: {},
    notice: '',
    isGift: function (item) {
      return !!(item && item.properties && item.properties._promotion);
    },
    /* "Free" only when Shopify has priced the line at 0; otherwise the line shows its normal price and the offer. */
    isFree: function (item) {
      var price = item && (item.final_line_price != null ? item.final_line_price : item.line_price);
      return price != null && Number(price) === 0;
    },
    label: function (item) {
      var record = this.isGift(item) && this.records[item.properties._promotion];
      var name = record && record.label ? record.label : '';
      var head = this.isFree(item) ? t('free_with_offer') : t('your_offer');
      return name ? head + ': ' + name : head;
    }
  };

  function ui() {
    try {
      return (window.Alpine && window.Alpine.store('ecwGift')) || store;
    } catch (error) {
      return store;
    }
  }

  function setNotice(message) {
    ui().notice = message || '';
  }

  function publish(state) {
    var live = liveRecords(state);
    var records = {};
    Object.keys(live).forEach(function (handle) {
      records[handle] = { label: live[handle].label };
    });
    ui().records = records;
  }

  function register() {
    if (window.Alpine && window.Alpine.store && !window.Alpine.store('ecwGift')) {
      window.Alpine.store('ecwGift', store);
    }
  }
  if (window.Alpine) register();
  else document.addEventListener('alpine:init', register);

  /* The theme's order table finds "this variant's line" in cart.variantMap. A free line must never be that line, or a
     quantity typed in the table changes the free line (and the next pass sets it back). Keystone's sortCartItems builds
     the map; this wraps it on the theme's own data, so the bundled vendor file stays stock. */
  function guardVariantMap() {
    try {
      var app = window.Alpine && window.Alpine.$data && window.Alpine.$data(document.documentElement);
      if (!app || !app.cart || typeof app.sortCartItems !== 'function') return;
      var fix = function (target) {
        var map = {};
        (target.cart.items || []).forEach(function (item) {
          if (!(item.properties && item.properties._promotion)) map[item.variant_id] = item;
        });
        target.cart.variantMap = map;
      };
      if (!app.sortCartItems.ecwGuarded) {
        var sort = app.sortCartItems;
        var guarded = function () {
          var result = sort.apply(this, arguments);
          fix(this);
          return result;
        };
        guarded.ecwGuarded = true;
        app.sortCartItems = guarded;
      }
      fix(app);
    } catch (error) {
      log('could not guard the order table', error);
    }
  }
  if (document.documentElement._x_dataStack) guardVariantMap();
  document.addEventListener('alpine:initialized', guardVariantMap);

  /* ---------- watching every cart change ---------- */

  function isMutation(url, method) {
    return String(method || 'GET').toUpperCase() === 'POST' && MUTATION.test(String(url || '').split('?')[0] + '?');
  }

  /* A cart note or attribute save (the note field posts on every keystroke) changes no line, so it needs no pass. */
  function changesNoLine(body) {
    if (typeof body !== 'string') return false;
    try {
      var data = JSON.parse(body);
      return !!data && typeof data === 'object' && Object.keys(data).every(function (key) {
        return key === 'note' || key === 'attributes';
      });
    } catch (error) {
      return false;
    }
  }

  window.fetch = function (input, init) {
    var url = input && typeof input === 'object' && 'url' in input ? input.url : String(input);
    var method = (init && init.method) || (input && typeof input === 'object' && input.method) || 'GET';
    var request = nativeFetch(input, init);
    if (isMutation(url, method) && !changesNoLine(init && init.body)) request.then(schedule, schedule);
    return request;
  };

  if (window.XMLHttpRequest) {
    var open = XMLHttpRequest.prototype.open;
    var send = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.ecwMutation = isMutation(url, method);
      return open.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function (body) {
      if (this.ecwMutation && !changesNoLine(body)) this.addEventListener('loadend', schedule);
      return send.apply(this, arguments);
    };
  }

  /* Hold checkout until a pass in flight has finished. Checkout prices the free line but never adds it, so a
     checkout started mid-pass (or a review draft, which takes its lines from the cart) could miss the free line. */
  function whenIdle(limit) {
    var started = Date.now();
    return new Promise(function (resolve) {
      (function check() {
        if (!busy() || Date.now() - started > limit) resolve();
        else setTimeout(check, 100);
      })();
    });
  }

  /* Checkout always waits for one fresh pass (within 8 s), so a free line a late write left over is corrected
     first; then the buyer's own click is replayed, so the theme's checkout button and cart form work as before. */
  var released = null;
  function holdCheckout(event, target) {
    if (released && released === target) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    enqueue(pass);
    whenIdle(8000).then(function () {
      if (target && target.isConnected && typeof target.click === 'function') {
        /* the replayed click and the submit it causes run in this same task, then the hold is back on */
        released = target;
        try {
          target.click();
        } finally {
          released = null;
        }
      } else {
        window.location.href = ROOT + 'checkout';
      }
    });
  }

  document.addEventListener('click', function (event) {
    var target = event.target && event.target.closest && event.target.closest('[name="checkout"], a[href$="/checkout"]');
    if (target) holdCheckout(event, target);
  }, true);

  /* A checkout submitted by keyboard; other posts of the cart form (Enter in a field) go through untouched. */
  document.addEventListener('submit', function (event) {
    if (event.submitter && event.submitter.name === 'checkout') holdCheckout(event, event.submitter);
  }, true);

  /* A pass on load covers changes made by a full page post (the cart page form) and earlier visits. */
  var initial = window.__initialData && window.__initialData.cart;
  if (!initial || initial.item_count > 0) schedule();
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) schedule();
  });

  window.ecwGift = { schedule: schedule, plan: plan, earnedUnits: earnedUnits };
})();
